import { type ReactNode } from "react";

import { Radio } from "antd";
import { type CheckboxOptionType } from "antd/lib/checkbox/Group";
import { type RadioGroupProps, type RadioProps } from "antd/lib/radio";

import { cn } from "@btnet/utilities-core";

import { BuiText } from "../BuiText/BuiText";
import "./BuiRadioGroup.less";

export type RadioOptionType = CheckboxOptionType & {
  /**
   * The caption text to display alongside the radio button.
   * This is used to provide additional context or information about the radio option.
   */
  caption?: string;
};

export type BuiRadioProps = RadioProps & {
  /**
   * The caption text to display alongside the radio button.
   * This is used to provide additional context or information about the radio option.
   */
  caption?: string;
};

export interface IBuiRadioGroupProps extends Omit<RadioGroupProps, "options"> {
  children?: ReactNode;
  /**
   * Whether the radio buttons should be displayed horizontally or vertically.
   * @default false
   */
  horizontal?: boolean;
  /**
   * @deprecated
   * this is no longer supported
   */
  displayBlock?: boolean;
  /**
   * @deprecated
   * this will be no longer supported
   */
  hasOptionDescriptions?: boolean;
  "data-testid"?: string;

  /**
   * Whether the radio group is in read-only mode.
   * In read-only mode, the radio buttons cannot be selected or changed.
   * @default false
   */
  readOnly?: boolean;

  /**
   * The options to be displayed in the radio group.
   * This can be an array of strings or an array of objects with value, label, and caption.
   */
  options?: (RadioOptionType | string)[];
}

/**
 * Get the radio options with captions.
 * @param options - The options to process.
 * @returns The processed options with captions.
 */
function getOptionsWithCaptions(options: (RadioOptionType | string)[]) {
  const hasCaptions = options.some(
    (option) => typeof option === "object" && "caption" in option,
  );

  const optionsWithCaptions =
    hasCaptions && options
      ? options.map((option) => {
          if (typeof option === "string") {
            return (
              <BuiRadio key={option} value={option}>
                {option}
              </BuiRadio>
            );
          } else {
            const { label, ...optionProps } = option;
            return (
              <BuiRadio
                key={option.value.toString()}
                {...optionProps}
                value={option.value}
              >
                {label}
              </BuiRadio>
            );
          }
        })
      : undefined;

  return optionsWithCaptions;
}

/**
 * @description
 * The BuiRadioGroup component.
 * BuiRadioGroup is a component that allows you to create a group of BuiRadio components and can be used in forms.
 *
 * For more info see storybook: @link https://blueprint-storybook.buildertrend.com/?path=/docs/components-buiradiogroup--documentation
 * Below are the params that can be passed to the component.
 * @param {ReactNode} children - The children of the component. This can be a list of BuiRadio components.
 * @param {boolean} horizontal - Determines whether or not the radio group should be displayed horizontally.
 * @param {string} className - The class name for the component.
 * @param {string} testid - The data-testid attribute for the component.
 * @param {function} onChange - The function to call when the value of the radio group changes.
 * @param {boolean} readOnly - Determines whether or not the radio group is read-only.
 * @param {Array<RadioOptionType | string>} options - The options to be displayed in the radio group. This can be an array of strings or an array of objects with value, label, and caption.
 * @returns {JSX.Element} The BuiRadioGroup component.
 */
export const BuiRadioGroup = ({
  children,
  horizontal = false,
  className,
  "data-testid": testid,
  onChange,
  readOnly,
  options,
  ...otherProps
}: IBuiRadioGroupProps) => {
  const optionsWithCaptions = options && getOptionsWithCaptions(options);

  return (
    <Radio.Group
      {...otherProps}
      className={cn(className, "bui-theme bui-radio-group", {
        "bui-radio-group--horizontal": horizontal,
        "bui-radio-group--readonly": readOnly,
      })}
      data-testid={testid}
      disabled={readOnly || otherProps.disabled || false}
      {...(onChange ? { onChange } : {})}
      {...(options && !optionsWithCaptions ? { options } : {})}
    >
      {optionsWithCaptions ?? children}
    </Radio.Group>
  );
};

/**
 * @description
 * The BuiRadio component.
 * BuiRadio is a component that allows you to create a radio button with an optional caption. Used within BuiRadioGroup.
 *
 * For more info see storybook: @link https://blueprint-storybook.buildertrend.com/?path=/docs/components-buiradiogroup--documentation
 * @param props - The props for the BuiRadio component.
 * @returns The BuiRadio component.
 */
export const BuiRadio = ({
  caption,
  children,
  className,
  ...radioProps
}: BuiRadioProps) => {
  const textWithCaption = (
    <div
      className={cn("flex flex-col items-start", "bui-radio-col-with-caption")}
    >
      {children}

      <BuiText text={caption} className="bui-radio-caption" />
    </div>
  );

  return (
    <Radio {...radioProps} className={cn(className, "bui-radio")}>
      {caption ? textWithCaption : children}
    </Radio>
  );
};
